import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon, 
  AcademicCapIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  PlusIcon,
  EyeIcon,
  DocumentTextIcon,
  IdentificationIcon,
  DocumentCheckIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getMyCompetencies } from "../../../../api/hse/competency";
import { CompetencyQuickStart } from "./quick-start";
import { CompetencySidebar } from "./competency-sidebar";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency My View - User's own competencies
 * Shows only the current user's competencies
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyMyView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    statuses: [],
    types: [],
  });
  const [showQuickStart, setShowQuickStart] = useState(false);
  
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });
  const canViewAll = isAdmin || canManage;

  useEffect(() => {
    fetchMyCompetencies();
    // Show quick start if user has no competencies and can manage
    if (competencies.length === 0 && canManage) {
      setShowQuickStart(true);
    }
  }, [filters]);

  useEffect(() => {
    if (competencies.length > 0) {
      setShowQuickStart(false);
    }
  }, [competencies]);

  const fetchMyCompetencies = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        search: filters.query,
        status: filters.statuses.length > 0 ? filters.statuses[0] : undefined,
        type: filters.types.length > 0 ? filters.types[0] : undefined,
      };
      
      const response = await getMyCompetencies(apiFilters);
      setCompetencies(response.competencies || []);
    } catch (err) {
      console.error("Error fetching competencies:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kompetanse"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      statuses: newFilters.statuses || [],
      types: newFilters.types || [],
    });
  };

  const getStatusBadge = (competency) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    const now = new Date();
    const expiresAt = competency.expires_at ? new Date(competency.expires_at) : null;
    
    if (!expiresAt) {
      return (
        <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          {t("Permanent")}
        </span>
      );
    }
    
    const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          {t("Utløpt")}
        </span>
      );
    } else if (daysUntilExpiry <= 30) {
      return (
        <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
          <ClockIcon className="h-3 w-3 mr-1" />
          {t("Utløper snart")}
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-green-100 text-green-800`}>
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          {t("Gyldig")}
        </span>
      );
    }
  };

  // Competency types for filtering
  const competencyTypes = [
    { value: "cv", label: t("CV"), icon: DocumentTextIcon },
    { value: "certificate", label: t("Sertifikat"), icon: DocumentCheckIcon },
    { value: "course", label: t("Kurs"), icon: AcademicCapIcon },
    { value: "license", label: t("Lisens"), icon: IdentificationIcon },
    { value: "other", label: t("Annet"), icon: DocumentTextIcon },
  ];

  // Status options for ModuleFilter
  const statusOptions = [
    { value: "active", label: t("Gyldig"), class: "bg-green-100 text-green-800" },
    { value: "expiring_soon", label: t("Utløper snart"), class: "bg-yellow-100 text-yellow-800" },
    { value: "expired", label: t("Utløpt"), class: "bg-red-100 text-red-800" },
    { value: "permanent", label: t("Permanent"), class: "bg-gray-100 text-gray-800" },
  ];

  // Custom dropdown filter for competency types
  const customDropdownFilters = [
    {
      key: "types",
      label: t("Type"),
      icon: DocumentTextIcon,
      options: competencyTypes.map(type => ({
        value: type.value,
        label: type.label,
        class: "bg-blue-100 text-blue-800",
      })),
    },
  ];

  const stats = {
    total: competencies.length,
    active: competencies.filter(c => {
      const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
      if (!expiresAt) return true;
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 30;
    }).length,
    expiring_soon: competencies.filter(c => {
      const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
      if (!expiresAt) return false;
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
    }).length,
    expired: competencies.filter(c => {
      const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
      if (!expiresAt) return false;
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry < 0;
    }).length,
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kompetanse")]} />;
  }

  return (
    <DashboardHeader
      title={t("Min kompetanse")}
      subtitle={t("Oversikt over din egen kompetanse og kurs")}
    >
      <div className="flex h-[calc(100vh-200px)] mt-8">
        {/* Sidebar - Only show for users with management rights */}
        {canManage && <CompetencySidebar />}
        
        {/* Main Content */}
        <div className={`flex-1 overflow-y-auto ${canManage ? 'pl-6' : ''}`}>
          <div className="mx-auto max-w-7xl px-6">
            {/* Quick Start Guide */}
            {showQuickStart && (
              <div className="mb-6">
                <CompetencyQuickStart onDismiss={() => setShowQuickStart(false)} />
              </div>
            )}
          
            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-4 mb-6">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AcademicCapIcon className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Totalt")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Gyldige")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.active}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-8 w-8 text-yellow-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Utløper snart")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.expiring_soon}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Utløpte")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.expired}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            </div>

            {/* ModuleFilter */}
            <div className="mb-6">
              <ModuleFilter
                onFiltersChange={handleFiltersChange}
                initialFilters={filters}
                searchPlaceholder={t("Søk i kompetanse...")}
                acceptedStatuses={statusOptions.map(s => s.value)}
                customDropdownFilters={customDropdownFilters}
              />
            </div>

            {/* My Competencies List */}
            <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("Mine kompetanser")}</h3>
                <RoleOnly role="hse_ks_competency_manage" action="read">
                  <Link
                    to="/dashboard/hse-ks/competency/matrix"
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {t("Se alle")} →
                  </Link>
                </RoleOnly>
              </div>
              
              {competencies.length === 0 ? (
                <ResourceEmpty message={t("Ingen kompetanser registrert")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Kompetanse")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utstedt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utløper")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {competencies.map((competency) => (
                        <tr key={competency.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {competency.type === "cv" && <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-3" />}
                              {competency.type === "certificate" && <DocumentCheckIcon className="h-5 w-5 text-green-600 mr-3" />}
                              {competency.type === "course" && <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-3" />}
                              {competency.type === "license" && <IdentificationIcon className="h-5 w-5 text-orange-600 mr-3" />}
                              {(!competency.type || competency.type === "other") && <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3" />}
                              <div>
                                <div className="font-medium text-gray-900">
                                  {competency.competency_definition?.name || competency.name || competency.title}
                                </div>
                                {competency.competency_definition?.description && (
                                  <div className="text-gray-500 text-xs mt-1">{competency.competency_definition.description}</div>
                                )}
                                {competency.type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    {competencyTypes.find(t => t.value === competency.type)?.label || competency.type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {getStatusBadge(competency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {competency.issued_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(competency.issued_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {competency.expires_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(competency.expires_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">{t("Permanent")}</span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/dashboard/hse-ks/competency/definitions/${competency.competency_definition_id || competency.id}`}
                              className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              {t("Vis")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

